import type { JobTimelinePoint } from '@zyx/shared';
import { useState } from 'react';

const HEIGHT = 120;

function stepLabel(stepMs: number): string {
  const minutes = Math.round(stepMs / 60_000);
  return minutes % 60 === 0 ? `${minutes / 60} h` : `${minutes} min`;
}

/**
 * Runs finished per bucket (a minute, or wider for a long job): succeeded and failed
 * stacked, a 2px surface gap between the two, a legend (two series) and a hover readout
 * per bucket. Quiet buckets are shown as gaps, since the x-axis is time.
 */
export function TimelineChart(props: { readonly points: ReadonlyArray<JobTimelinePoint>; readonly stepMs: number }) {
  const [hover, setHover] = useState<number | undefined>(undefined);
  const { points, stepMs } = props;
  if (points.length === 0) {
    return <p className="text-sm text-muted-foreground">No runs recorded yet.</p>;
  }
  const first = points[0].at;
  const minutes = Math.max(1, Math.round((points[points.length - 1].at - first) / stepMs) + 1);
  const peak = Math.max(...points.map((point) => point.succeeded + point.failed), 1);
  const width = Math.max(minutes * 8, 240);
  const barWidth = Math.max(2, width / minutes - 2);
  const hovered = hover === undefined ? undefined : points[hover];
  const y = (value: number) => (value / peak) * (HEIGHT - 4);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-primary" /> Succeeded
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-destructive" /> Failed or interrupted
        </span>
        <span className="ml-auto tabular-nums">
          peak {peak} per {stepLabel(stepMs)}
        </span>
      </div>
      <div className="relative overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${HEIGHT}`}
          preserveAspectRatio="none"
          className="h-32 w-full min-w-[240px]"
          role="img"
          aria-label={`Runs per ${stepLabel(stepMs)} over ${minutes} buckets, peaking at ${peak}`}
        >
          <line x1={0} x2={width} y1={HEIGHT - 0.5} y2={HEIGHT - 0.5} className="stroke-border" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          {points.map((point, index) => {
            const x = ((point.at - first) / stepMs) * (width / minutes);
            const ok = y(point.succeeded);
            const bad = y(point.failed);
            return (
              <g key={point.at} onMouseEnter={() => setHover(index)} onMouseLeave={() => setHover(undefined)}>
                <rect x={x} y={0} width={width / minutes} height={HEIGHT} fill="transparent" />
                {ok > 0 ? <rect x={x + 1} y={HEIGHT - ok} width={barWidth} height={ok} rx={1} className="fill-primary" /> : null}
                {bad > 0 ? <rect x={x + 1} y={HEIGHT - ok - bad - (ok > 0 ? 2 : 0)} width={barWidth} height={bad} rx={1} className="fill-destructive" /> : null}
              </g>
            );
          })}
        </svg>
        {hovered === undefined ? null : (
          <div className="pointer-events-none absolute right-2 top-0 rounded border border-border bg-popover px-2 py-1 text-xs text-popover-foreground shadow">
            {new Date(hovered.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {hovered.succeeded} succeeded · {hovered.failed} failed
          </div>
        )}
      </div>
    </div>
  );
}
